import * as THREE from 'three';
import { OrbitControls }   from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer }  from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass }      from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass }      from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass }      from 'three/examples/jsm/postprocessing/OutputPass.js';

import { EnvironmentSphere } from './EnvironmentSphere.js';
import { TerrainMesh }       from './TerrainMesh.js';
import { ParticleSystem }    from './ParticleSystem.js';
import { AmbientParticles }  from './AmbientParticles.js';
import { ShieldDome }        from './ShieldDome.js';
import { FlagDome }          from './FlagDome.js';
import { UAEFlag }           from './UAEFlag.js';
import { DARK_MATERIAL }     from './glsl.js';

// ===== INITIAL CAMERA POSITION =====
// Change these to set where the camera starts.
const CAMERA_START_X = 500;
const CAMERA_START_Y = 80;
const CAMERA_START_Z = 355;

// The point the camera looks at (target).
const CAMERA_TARGET_X = 30;
const CAMERA_TARGET_Y = 55;
const CAMERA_TARGET_Z = 0;
// ===================================

const MIX_VERT = `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}`;
const MIX_FRAG = `
uniform sampler2D baseTexture;
uniform sampler2D bloomTexture;
varying vec2 vUv;
void main(){
  gl_FragColor = texture2D(baseTexture,vUv) + texture2D(bloomTexture,vUv);
}`;

export class SceneManager {
  constructor(canvas) {
    this.canvas      = canvas;
    this.timer       = new THREE.Timer();
    this.smoothStage = 0;
    this.targetStage = 0;
    this.config      = { curlSpeed: 1.0, bloom: 0.85 };
    this._animId     = null;
    this._onResizeFn = null;
    this.environment = null; this.terrain  = null; this.particles = null;
    this.ambient     = null; this.skyline  = null; this.shield   = null;
    this.flagDome    = null; this.uaeFlag  = null;
    this._savedMats  = {};
  }

  init() {
    this._setupRenderer();
    this._setupScene();
    this._setupCamera();
    this._setupLights();
    this._setupPostFX();
    this._setupSystems();
    this._setupResize();
    this._loop();
  }

  updateStats(stats, stage, atmosphere) {
    this.targetStage = stage;
    if (atmosphere) {
      this.config.curlSpeed = atmosphere.curlSpeed ?? 1.0;
      this.config.bloom     = atmosphere.bloom     ?? 1.5;
    }
    this.flagDome?.updateFlags(stats.nationalities || {}, this.smoothStage);
  }

  addFlagForFirstContribution(nationality) {
    this.flagDome?.addFlagForFirstContribution(nationality);
  }

  setOrbitEnabled(en) { if (this.controls) this.controls.enabled = en; }

  _darkenForBloom() {
    this.scene.traverse(obj => {
      if (obj.isMesh && obj.userData.noBloom) {
        this._savedMats[obj.uuid] = obj.material;
        obj.material = DARK_MATERIAL;
      }
    });
  }
  _restoreFromBloom() {
    this.scene.traverse(obj => {
      if (this._savedMats[obj.uuid]) {
        obj.material = this._savedMats[obj.uuid];
        delete this._savedMats[obj.uuid];
      }
    });
  }

  _setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas, antialias: true, powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.toneMapping         = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.timer.connect(this.renderer);
  }

  _setupScene() {
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x0A0510, 0.00072);
  }

  _setupCamera() {
    const isMobile = window.innerWidth <= 768;

    // ===== CAMERA START POSITION =====
    const camX = isMobile ? 800  : CAMERA_START_X;
    const camY = isMobile ? 80  : CAMERA_START_Y;
    const camZ = isMobile ? 355  : CAMERA_START_Z;
    // =================================

    // ===== CAMERA TARGET (lookAt) =====
    const tgtX = isMobile ? 30   : CAMERA_TARGET_X;
    const tgtY = isMobile ? 200   : CAMERA_TARGET_Y;
    const tgtZ = isMobile ? 0    : CAMERA_TARGET_Z;
    // ==================================

    // ===== CAMERA VERTICAL LIMITS =====
    const minPolar = isMobile ? 1.2          : 0.6;
    const maxPolar = isMobile ? Math.PI * 0.5  : Math.PI * 0.5;
    // ===================================

    // ===== ZOOM LIMITS =====
    const minDist = isMobile ? 300 : 300;
    const maxDist = isMobile ? 700 : 700;
    // =======================

    this.camera = new THREE.PerspectiveCamera(58, window.innerWidth / window.innerHeight, 1, 2200);
    this.camera.position.set(camX, camY, camZ);
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.target.set(tgtX, tgtY, tgtZ);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;

    this.controls.minPolarAngle = minPolar;
    this.controls.maxPolarAngle = maxPolar;

    this.controls.minDistance = minDist;
    this.controls.maxDistance = maxDist;

    this.controls.update();
  }

  _setupLights() {
    this.scene.add(new THREE.AmbientLight(0x5A3510, 0.7));
    const sun = new THREE.DirectionalLight(0xF8AA38, 1.8);
    sun.position.set(100, 210, 110);
    this.scene.add(sun);
    const fill = new THREE.DirectionalLight(0x200A40, 0.45);
    fill.position.set(-80, 100, -50);
    this.scene.add(fill);
    this._cityGlow = new THREE.PointLight(0xFFAA30, 2.2, 320);
    this._cityGlow.position.set(0, 55, 0);
    this.scene.add(this._cityGlow);
  }

  _setupPostFX() {
    const sz = new THREE.Vector2(window.innerWidth, window.innerHeight);

    this.bloomComposer = new EffectComposer(this.renderer);
    this.bloomComposer.renderToScreen = false;
    this.bloomComposer.addPass(new RenderPass(this.scene, this.camera));
    this.bloomPass = new UnrealBloomPass(sz, 0.85, 0.35, 0.90);
    this.bloomComposer.addPass(this.bloomPass);

    const mixPass = new ShaderPass(
      new THREE.ShaderMaterial({
        uniforms: {
          baseTexture:  { value: null },
          bloomTexture: { value: this.bloomComposer.renderTarget2.texture },
        },
        vertexShader:   MIX_VERT,
        fragmentShader: MIX_FRAG,
        defines: {},
      }),
      'baseTexture',
    );
    mixPass.needsSwap = true;

    this.finalComposer = new EffectComposer(this.renderer);
    this.finalComposer.addPass(new RenderPass(this.scene, this.camera));
    this.finalComposer.addPass(mixPass);
    this.finalComposer.addPass(new OutputPass());
  }

  _setupSystems() {
    this.environment = new EnvironmentSphere(this.scene);
    this.terrain     = new TerrainMesh(this.scene);
    this.particles   = new ParticleSystem(this.scene);
    this.ambient     = new AmbientParticles(this.scene);
    this.shield      = new ShieldDome(this.scene);
    this.flagDome    = new FlagDome(this.scene);
    this.uaeFlag     = new UAEFlag(this.scene);

    this.environment.init();
    this.terrain.init();
    this.ambient.init();
    this.shield.init();
    this.flagDome.init();
    this.uaeFlag.init();

    this.particles.init().catch(err => {
      console.error('ParticleSystem init failed:', err);
    });
  }

  _setupResize() {
    this._onResizeFn = () => {
      const w = window.innerWidth, h = window.innerHeight;
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, h, false);
      this.bloomComposer.setSize(w, h);
      this.finalComposer.setSize(w, h);
    };
    window.addEventListener('resize', this._onResizeFn);
  }

  _loop() {
    this._animId = requestAnimationFrame(() => this._loop());
    this.timer.update();
    const delta = this.timer.getDelta();
    const time  = this.timer.getElapsed();

    this.smoothStage += (this.targetStage - this.smoothStage) * 0.015;

    this.bloomPass.strength = this.config.bloom * (0.4 + this.smoothStage * 0.10);
    this._cityGlow.intensity = 1.2 + this.smoothStage * 0.55 + Math.sin(time * 0.85) * 0.28;

    this.environment.update(time);
    this.terrain.update(time);
    this.particles.update(time, this.smoothStage, this.config.curlSpeed);
    this.ambient.update(time, this.config.curlSpeed);
    this.shield.update(time, this.smoothStage);
    this.flagDome.update(time, this.smoothStage, delta);
    this.uaeFlag.update(time);

    this.controls.update();

    this._darkenForBloom();
    this.bloomComposer.render();
    this._restoreFromBloom();

    this.finalComposer.render();
  }

  dispose() {
    cancelAnimationFrame(this._animId);
    window.removeEventListener('resize', this._onResizeFn);
    this.controls.dispose();
    this.renderer.dispose();
  }
}